/** @format */

import { createFeatureSelector, createSelector } from '@ngrx/store';

import { ProductSelectionState } from '@pdp/data-access/interfaces';
import { Warranty } from '@shared/data-access/interfaces';
import {
	selectAllProductSkus,
	selectIsSingleSkuProduct,
	selectResolvedSku,
	selectTemplateName
} from '../product-selection.selectors';
import { DynamicProductTemplateModules, WarrantyButtonState } from '@pdp/data-access/enums';
import { selectAllKillswitches } from '../killswitches/killswitches.selectors';
// circular dep workaround
import { selectWarrantyAddToExistingCartError } from '../../../../../add-to-cart/data-access/src/lib/+state/add-to-cart.selectors';

export const selectState = createFeatureSelector<ProductSelectionState>('productSelection');

export const selectWarrantiesState = createSelector(selectState, (state) => state?.warranties);

export const selectSelectedWarranty = createSelector(
	selectWarrantiesState,
	(state) => state?.selectedWarranty
);

export const selectWarrantyCallState = createSelector(
	selectWarrantiesState,
	(state) => state?.warrantyCallState
);

export const selectAllWarranties = createSelector(
	selectWarrantiesState,
	(state) => state?.allWarranties
);

export const selectSelectedWarrantyPlanSku = createSelector(
	selectSelectedWarranty,
	(selectedWarranty) => selectedWarranty?.planSku
);

export const selectResolvedOrOnlySku = createSelector(
	selectResolvedSku,
	selectIsSingleSkuProduct,
	selectAllProductSkus,
	(resolvedSku, isSingleSkuProduct, allProductSkus) => {
		// For single sku products, we don't care if it's resolved or not
		if (isSingleSkuProduct) {
			return allProductSkus[0];
		}
		return resolvedSku;
	}
);

export const selectWarrantiesForResolvedSku = createSelector(
	selectAllWarranties,
	selectResolvedOrOnlySku,
	(allWarranties, resolvedSku): Warranty[] => {
		if (!resolvedSku || !allWarranties) {
			return [];
		}
		return allWarranties[resolvedSku.partNumber] ?? [];
	}
);

export const selectAemContentIdentifier = createSelector(
	selectWarrantiesForResolvedSku,
	(warrantiesForResolvedSku): string | undefined => {
		return warrantiesForResolvedSku?.[0]?.aemContentIdentifier;
	}
);

export const selectSelectedWarrantyDetails = createSelector(
	selectSelectedWarranty,
	selectWarrantiesForResolvedSku,
	(selectedWarranty, warranties) => {
		if (!selectedWarranty || !warranties?.length) {
			return undefined;
		}
		return warranties.find((w) => w.planSku === selectedWarranty.planSku);
	}
);

export const selectIsProductWarrantyEligible = createSelector(
	selectAllProductSkus,
	(allSkus) => !!allSkus?.some((sku) => !!sku?.warrantyEligible)
);

export const selectIsResolvedSkuWarrantyEligible = createSelector(
	selectResolvedOrOnlySku,
	(resolvedSku) => !!resolvedSku?.warrantyEligible
);

export const selectWarrantyButtonState = createSelector(
	selectResolvedOrOnlySku,
	selectIsResolvedSkuWarrantyEligible,
	selectSelectedWarranty,
	(resolvedSku, isResolvedSkuWarrantyEligible, selectedWarranty) => {
		if (!resolvedSku) {
			return WarrantyButtonState.BeforeSkuResolve;
		}

		if (!isResolvedSkuWarrantyEligible) {
			return WarrantyButtonState.WarrantyNotAvailable;
		}

		if (selectedWarranty) {
			return WarrantyButtonState.WarrantySelected;
		}

		return WarrantyButtonState.WarrantyAvailable;
	}
);

export const selectWarrantyCallMadeButNoWarranties = createSelector(
	selectWarrantiesForResolvedSku,
	selectWarrantyCallState,
	(warrantiesForResolvedSku, warrantyCallState) =>
		warrantyCallState === 'success' && warrantiesForResolvedSku?.length === 0
);

export const selectWarrantyAnalyticsEcodes = createSelector(
	selectWarrantiesForResolvedSku,
	(warrantiesForResolvedSku): string => {
		return (
			warrantiesForResolvedSku
				?.map((w) => w.planEcode)
				?.filter((ecode) => !!ecode)
				?.join('|') ?? ''
		);
	}
);

export const selectShouldShowWarrantySelectorOnAtc = createSelector(
	selectAllKillswitches,
	selectTemplateName,
	selectIsResolvedSkuWarrantyEligible,
	selectWarrantyCallMadeButNoWarranties,
	selectSelectedWarranty,
	selectWarrantyAddToExistingCartError,
	(
		killswitches,
		templateName,
		selectIsResolvedSkuWarrantyEligible,
		warrantyCallMadeButNoWarranties,
		selectedWarranty,
		warrantyAddToExistingCartError
	) =>
		killswitches.warrantiesEnabled &&
		killswitches.warrantiesSelectionOnAtcEnabled &&
		templateName === DynamicProductTemplateModules.IN_STOCK_DEFAULT &&
		selectIsResolvedSkuWarrantyEligible &&
		selectedWarranty === undefined && // ensure user hasn't already selected a warranty (including explicitly selecting No Plan)
		!warrantyCallMadeButNoWarranties && // and that we have warranties to show
		!warrantyAddToExistingCartError
);
